import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface ClueInputProps {
  onSubmit: (clue: string) => void;
  maxWords?: number;
  placeholder?: string;
}

export function ClueInput({ onSubmit, maxWords = 2, placeholder }: ClueInputProps) {
  const [clue, setClue] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedClue = clue.trim();
    const wordCount = trimmedClue.split(/\s+/).filter(word => word.length > 0).length;
    
    if (wordCount === 0) {
      setError('Please enter a clue');
      return;
    }
    
    if (wordCount > maxWords) {
      setError(`Clue must be ${maxWords} word${maxWords > 1 ? 's' : ''} or less`);
      return;
    }
    
    onSubmit(trimmedClue);
    setClue('');
    setError('');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setClue(e.target.value);
    if (error) setError('');
  };
  
  // Calculate current word count for display
  const currentWordCount = clue.trim().split(/\s+/).filter(word => word.length > 0).length;

  return (
    <form onSubmit={handleSubmit} className="max-w-md mx-auto">
      <div className="space-y-4">
        <div>
          <label htmlFor="clue" className="block text-sm font-medium text-gray-700 mb-2">
            Enter your clue (1-{maxWords} words)
          </label>
          <div className="relative">
            <input
              type="text"
              id="clue"
              value={clue}
              onChange={handleChange}
              placeholder={placeholder || "e.g., Ocean, Sunset Sky"}
              className="w-full px-4 py-2 pr-16 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
              autoFocus
            />
            <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-sm ${
              currentWordCount > maxWords ? 'text-red-600' : 'text-gray-500'
            }`}>
              {currentWordCount}/{maxWords}
            </span>
          </div>
        </div>
        
        <AnimatePresence>
          {error && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="text-red-600 text-sm"
            >
              {error}
            </motion.p>
          )}
        </AnimatePresence>
        
        <button
          type="submit"
          className="w-full px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium"
        >
          Submit Clue
        </button>
      </div>
    </form>
  );
}